import { createAsyncThunk } from "@reduxjs/toolkit";

import { RootState } from "../../app/store";

import { TempPasswordUpdated } from "../../common/components/functional/tempPasswordDialog/TempPasswordDialog";
import { handleExpenseTypesData } from "./helpers/utils";
import {
  addEezyCompanyOrderQueue,
  addOrderQueue,
  deleteOrderQueue,
  EditOrderQueue,
  listCities,
  listOrderQueueById,
  listOrderQueues,
  OrderQueue,
  OrderQueueEvent,
  postEditOrderQueue,
  PostEezyCompanyOrderQueue,
  PostOrderQueue,
} from "./orderQueues/orderQueueApi";
import { OrderQueueOption } from "./orderQueues/types";
import {
  AcceptWorkAgreementPayload,
  AcceptWorkOffer,
  Accounts,
  addExpenseTypeMapping,
  addNewSchedulingPeriod,
  AutoApproveEntriesPayload,
  bulkSalaryIncreaseQuery,
  bulkSalaryIncreaseStateQuery,
  CancelShift,
  CreateGarnishmentPaymentPayload,
  CreateHolidayBacklogReportsPayload,
  CreatePayerSummaryReportDeliveryPayload,
  CreateSalaryPaymentPayload,
  CreateTaxPaymentPayload,
  CreateTradeUnionPaymentPayload,
  CreateWageReportDeliveryPayload,
  DeleteWageReportDeliveryPayload,
  ExpenseTypeMapping,
  ExportGarnishmentPaymentPayload,
  ExportTaxPaymentPayload,
  ExportTradeUnionPaymentPayload,
  getAccounts,
  getExpenseTypeMappings,
  getExpenseTypeMappingsForDate,
  getIncomeTypes,
  getSalesAccountConfigDates,
  getSalesAccountConfigForDate,
  getSchedulingPeriodsBySchedulingClassAndYear,
  getTesPriceList,
  IncomeTypeFilters,
  IncomeTypes,
  inspectEvents,
  InspectEventsFormValues,
  InspectEventsResponse,
  ProcessWorkOffersPayload,
  RebuildWorkHistoryPayload,
  RecalculateCustomerPricingPayload,
  RedactTalentDataPayload,
  runAcceptWorkAgreement,
  runAcceptWorkOffer,
  runApprovePayslips,
  runAutoApproveEntries,
  runCancelShift,
  runCreateGarnishmentPayment,
  runCreateHolidayBacklogReports,
  runCreateInvoices,
  runCreatePayerSummaryReportDelivery,
  runCreatePayslips,
  runCreateSalaryPayment,
  runCreateTaxPayment,
  runCreateTradeUnionPayment,
  runCreateWageReportDelivery,
  runDeleteInvoice,
  runDeleteWageReportDelivery,
  runExportGarnishmentPayment,
  runExportTaxPayment,
  runExportTradeUnionPayment,
  runLockInvoices,
  runLockPayslips,
  runProcessWorkOffers,
  runRebuildWorkHistoryTimes,
  runRecalculateCustomerPricing,
  runRedactTalentData,
  runSendInvoices,
  runSetTalentTemporaryPassword,
  runUpdateInvoice,
  runUpdatePayerSummaryReportDeliveryStatus,
  runUpdateTalentEmailAddress,
  runUpdateTalentStatus,
  runUpdateTesPriceList,
  runUpdateWageReportDeliveryStatus,
  SalesAccountConfigFilters,
  SalesAccounts,
  SalesAccountsPost,
  saveAccounts,
  saveIncomeTypes,
  saveSalesAccounts,
  SchedulingClassAndDatePayload,
  SchedulingPeriodEvent,
  SchedulingPeriodPost,
  SchedulingPeriods,
  SendInvoicesPayload,
  SetTalentTemporaryPassword,
  TesPriceList,
  updateExpenseTypeMapping,
  UpdateInvoicePayload,
  updateMassUpdateClarities,
  updateOneSchedulingPeriod,
  UpdatePayerSummaryReportDeliveryStatusPayload,
  UpdateTalentEmailAddressPayload,
  UpdateTalentStatusPayload,
  UpdateTesPriceList,
  UpdateTesPriceListEvent,
  UpdateWageReportDeliveryStatusPayload,
} from "./settingsApi";
import {
  BulkSalaryIncreaseQuery,
  MassUpdateClarityPayload,
  MassUpdateStatus,
  MassUpdateStatusQuery,
  SalaryRows,
} from "./types/queries";

export const fetchIncomeTypes = createAsyncThunk<
  IncomeTypes,
  IncomeTypeFilters,
  { state: RootState }
>("settings/getIncomeTypes", async (filters: {}, { requestId }) => {
  const response = await getIncomeTypes({ requestId });
  return response.data;
});

export const updateIncomeTypes = createAsyncThunk<
  IncomeTypes,
  IncomeTypes,
  { state: RootState }
>(
  "settings/updateIncomeTypes",
  async (incomeTypes: IncomeTypes, { requestId }) => {
    const response = await saveIncomeTypes(incomeTypes, { requestId });
    const data = {
      incomeTypes: response.data[0].payload.incomeTypes,
      version: response.data[0].version,
    };
    return data;
  },
);

export const fetchAccounts = createAsyncThunk<
  Accounts,
  IncomeTypeFilters,
  { state: RootState }
>("settings/getAccounts", async (filters: {}, { requestId }) => {
  const response = await getAccounts({ requestId });
  return response.data;
});

export const updateAccounts = createAsyncThunk<
  Accounts,
  Accounts,
  { state: RootState }
>("settings/updateAccounts", async (accounts: Accounts, { requestId }) => {
  const response = await saveAccounts(accounts, { requestId });
  const data = {
    accounts: response.data[0].payload.accounts,
    version: response.data[0].version,
  };
  return data;
});

export const fetchSalesAccountConfigDates = createAsyncThunk<
  string[],
  null,
  { state: RootState }
>("settings/fetchSalesAccountConfigDates", async () => {
  const response = await getSalesAccountConfigDates();
  return response.data;
});

export const fetchSalesAccountConfigForDate = createAsyncThunk<
  SalesAccounts,
  SalesAccountConfigFilters,
  { state: RootState }
>(
  "settings/fetchSalesAccountConfigForDate",
  async (validFrom: SalesAccountConfigFilters) => {
    const response = await getSalesAccountConfigForDate(validFrom);
    return response.data;
  },
);

export const updateSalesAccounts = createAsyncThunk<
  SalesAccountsPost,
  SalesAccounts,
  { state: RootState }
>(
  "settings/updateSalesAccounts",
  async (accounts: SalesAccounts, { requestId }) => {
    const response = await saveSalesAccounts(accounts, { requestId });
    const data = {
      accounts: response.data[0].accounts,
      validFrom: response.data[0].validFrom,
      version: response.data[0].version,
    };
    return data;
  },
);

export const fetchSalaries = createAsyncThunk<
  SalaryRows,
  BulkSalaryIncreaseQuery,
  { state: RootState }
>(
  "settings/fetchSalaries",
  async (filters: BulkSalaryIncreaseQuery, { requestId }) =>
    await bulkSalaryIncreaseQuery(filters, { requestId }),
);

/**
 * This is actually amend, not set..
 */
export const setMassUpdateState = createAsyncThunk<
  any,
  MassUpdateClarityPayload,
  { state: RootState }
>("settings/setMassUpdateState", async (payload: any, { requestId }) => {
  // This is a placeholder function. Implement the actual API call as needed.
  // return { data: "Mass update state set", payload };
  const response = await updateMassUpdateClarities(payload);
  return response.data;
});

export const fetchMassUpdateState = createAsyncThunk<
  MassUpdateStatus,
  MassUpdateStatusQuery,
  { state: RootState }
>(
  "settings/fetchMassUpdateState",
  async (query: MassUpdateStatusQuery, { requestId }) =>
    await bulkSalaryIncreaseStateQuery(query, { requestId }),
);

export const fetchExpenseTypeMappings = createAsyncThunk<
  string[],
  {},
  { state: RootState }
>("settings/getExpenseTypeMappings", async (filters: {}, { requestId }) => {
  const response = await getExpenseTypeMappings({ requestId });
  return response.data;
});

export const fetchExpenseTypeMappingsForDate = createAsyncThunk<
  ExpenseTypeMapping,
  { date: string },
  { state: RootState }
>("settings/getExpenseTypeMappingForDate", async ({ date }, { requestId }) => {
  const response = await getExpenseTypeMappingsForDate(date, { requestId });
  const expenseTypes = handleExpenseTypesData(response.data.expenseTypes);
  const data: ExpenseTypeMapping = {
    validFrom: response.data.validFrom,
    expenseTypes,
  };
  return data;
});

export const addExpenseTypes = createAsyncThunk<
  ExpenseTypeMapping,
  ExpenseTypeMapping,
  { state: RootState }
>(
  "settings/addExpenseTypeMapping",
  async (expenseTypeMapping: ExpenseTypeMapping, { requestId }) => {
    const response = await addExpenseTypeMapping(expenseTypeMapping, {
      requestId,
    });
    const { mapping, validFrom } = response.data[0].payload;
    const expenseTypes = handleExpenseTypesData(mapping);
    const data: ExpenseTypeMapping = {
      validFrom,
      expenseTypes,
    };
    return data;
  },
);

export const updateExpenseTypes = createAsyncThunk<
  ExpenseTypeMapping,
  ExpenseTypeMapping,
  { state: RootState }
>(
  "settings/updateExpenseTypeMapping",
  async (expenseTypeMapping: ExpenseTypeMapping, { requestId }) => {
    const response = await updateExpenseTypeMapping(expenseTypeMapping, {
      requestId,
    });
    const { mapping, validFrom } = response.data[0].payload;
    const expenseTypes = handleExpenseTypesData(mapping);
    const data: ExpenseTypeMapping = {
      validFrom,
      expenseTypes,
    };
    return data;
  },
);

export const fetchSchedulingPeriods = createAsyncThunk<
  SchedulingPeriods,
  { schedulingClassId: string; year: string },
  { state: RootState }
>(
  "settings/fetchSchedulingPeriods",
  async ({ schedulingClassId, year }, { requestId }) => {
    const response = await getSchedulingPeriodsBySchedulingClassAndYear(
      schedulingClassId,
      year,
      { requestId },
    );
    return response.data;
  },
);

export const updateSchedulingPeriod = createAsyncThunk<
  SchedulingPeriodEvent[],
  SchedulingPeriodPost,
  { state: RootState }
>(
  "settings/updateSchedulingPeriod",
  async (schedulingPeriod: SchedulingPeriodPost, { requestId }) => {
    const response = await updateOneSchedulingPeriod(schedulingPeriod, {
      requestId,
    });
    return response.data;
  },
);

export const addSchedulingPeriod = createAsyncThunk<
  SchedulingPeriodEvent[],
  SchedulingPeriodPost,
  { state: RootState }
>(
  "settings/addSchedulingPeriod",
  async (schedulingPeriod: SchedulingPeriodPost, { requestId }) => {
    const response = await addNewSchedulingPeriod(schedulingPeriod, {
      requestId,
    });
    return response.data;
  },
);

export const fetchOrderQueues = createAsyncThunk<
  OrderQueueOption[],
  void,
  { state: RootState }
>("settings/addSchedulingPeriod", async (_, { requestId }) => {
  const response = await listOrderQueues({
    requestId,
  });
  return response.data;
});

export const fetchOrderQueueById = createAsyncThunk<
  OrderQueue,
  string,
  { state: RootState }
>("settings/addSchedulingPeriod", async (id: string, { requestId }) => {
  const response = await listOrderQueueById(id, {
    requestId,
  });
  return response.data;
});

export const fetchCities = createAsyncThunk<
  string[],
  any,
  { state: RootState }
>("settings/fetchCities", async (_, { requestId }) => {
  const response = await listCities({
    requestId,
  });
  return response.data;
});

export const createOrderQueue = createAsyncThunk<
  OrderQueueEvent[],
  PostOrderQueue,
  { state: RootState }
>(
  "settings/createOrderQueue",
  async (orderQueue: PostOrderQueue, { requestId }) => {
    const response = await addOrderQueue(orderQueue, {
      requestId,
    });
    return response.data;
  },
);

export const createEezyCompanyOrderQueue = createAsyncThunk<
  OrderQueueEvent[],
  PostEezyCompanyOrderQueue,
  { state: RootState }
>(
  "settings/createEezyCompanyOrderQueue",
  async (orderQueue: PostEezyCompanyOrderQueue, { requestId }) => {
    const response = await addEezyCompanyOrderQueue(orderQueue, {
      requestId,
    });
    return response.data;
  },
);

export const editOrderQueue = createAsyncThunk<
  OrderQueueEvent[],
  EditOrderQueue,
  { state: RootState }
>(
  "settings/editOrderQueue",
  async (orderQueue: EditOrderQueue, { requestId }) => {
    const response = await postEditOrderQueue(orderQueue, {
      requestId,
    });
    return response.data;
  },
);

export const removeOrderQueue = createAsyncThunk<
  OrderQueueEvent[],
  string,
  { state: RootState }
>("settings/deleteOrderQueue", async (orderQueueId: string, { requestId }) => {
  const response = await deleteOrderQueue(orderQueueId, {
    requestId,
  });
  return response.data;
});

export const createInvoices = createAsyncThunk<
  any,
  SchedulingClassAndDatePayload,
  { state: RootState }
>("settings/createInvoices", async (payload: SchedulingClassAndDatePayload) => {
  const response = await runCreateInvoices(payload);
  return response.data;
});

export const lockInvoices = createAsyncThunk<
  any,
  SendInvoicesPayload,
  { state: RootState }
>("settings/lockInvoices", async (payload: SendInvoicesPayload) => {
  const response = await runLockInvoices(payload);
  return response.data;
});

export const sendInvoices = createAsyncThunk<
  any,
  SendInvoicesPayload,
  { state: RootState }
>("settings/sendInvoices", async (payload: SendInvoicesPayload) => {
  const response = await runSendInvoices(payload);
  return response.data;
});

export const createPayslips = createAsyncThunk<
  any,
  SchedulingClassAndDatePayload,
  { state: RootState }
>("settings/createPayslips", async (payload: SchedulingClassAndDatePayload) => {
  const response = await runCreatePayslips(payload);
  return response.data;
});

export const approvePayslips = createAsyncThunk<
  any,
  SchedulingClassAndDatePayload,
  { state: RootState }
>(
  "settings/approvePayslips",
  async (payload: SchedulingClassAndDatePayload) => {
    const response = await runApprovePayslips(payload);
    return response.data;
  },
);

export const lockPayslips = createAsyncThunk<
  any,
  SchedulingClassAndDatePayload,
  { state: RootState }
>("settings/lockPayslips", async (payload: SchedulingClassAndDatePayload) => {
  const response = await runLockPayslips(payload);
  return response.data;
});

export const createHolidayBacklogReports = createAsyncThunk<
  any,
  CreateHolidayBacklogReportsPayload,
  { state: RootState }
>(
  "settings/createHolidayBacklogReports",
  async (payload: CreateHolidayBacklogReportsPayload) => {
    const response = await runCreateHolidayBacklogReports(payload);
    return response.data;
  },
);

export const redactTalentData = createAsyncThunk<
  any,
  RedactTalentDataPayload,
  { state: RootState }
>("settings/redactTalentData", async (payload: RedactTalentDataPayload) => {
  const response = await runRedactTalentData(payload);
  return response.data;
});

export const autoApproveEntries = createAsyncThunk<
  any,
  AutoApproveEntriesPayload,
  { state: RootState }
>("settings/autoApproveEntries", async (payload: AutoApproveEntriesPayload) => {
  const response = await runAutoApproveEntries(payload);
  return response.data;
});

export const rebuildWorkHistoryTimes = createAsyncThunk<
  any,
  RebuildWorkHistoryPayload,
  { state: RootState }
>(
  "settings/rebuildWorkHistoryTimes",
  async (payload: RebuildWorkHistoryPayload) => {
    const response = await runRebuildWorkHistoryTimes(payload);
    return response.data;
  },
);

export const recalculateCustomerPricing = createAsyncThunk<
  any,
  RecalculateCustomerPricingPayload,
  { state: RootState }
>(
  "settings/recalculateCustomerPricing",
  async (payload: RecalculateCustomerPricingPayload) => {
    const response = await runRecalculateCustomerPricing(payload);
    return response.data;
  },
);

export const createSalaryPayment = createAsyncThunk<
  any,
  CreateSalaryPaymentPayload,
  { state: RootState }
>(
  "settings/createSalaryPayment",
  async (payload: CreateSalaryPaymentPayload) => {
    const response = await runCreateSalaryPayment(payload);
    return response.data;
  },
);

export const createTaxPayment = createAsyncThunk<
  any,
  CreateTaxPaymentPayload,
  { state: RootState }
>("settings/createTaxPayment", async (payload: CreateTaxPaymentPayload) => {
  const response = await runCreateTaxPayment(payload);
  return response.data;
});

export const exportTaxPayment = createAsyncThunk<
  any,
  ExportTaxPaymentPayload,
  { state: RootState }
>("settings/exportTaxPayment", async (payload: ExportTaxPaymentPayload) => {
  const response = await runExportTaxPayment(payload);
  return response.data;
});

export const createGarnishmentPayment = createAsyncThunk<
  any,
  CreateGarnishmentPaymentPayload,
  { state: RootState }
>(
  "settings/createGarnishmentPayment",
  async (payload: CreateGarnishmentPaymentPayload) => {
    const response = await runCreateGarnishmentPayment(payload);
    return response.data;
  },
);

export const exportGarnishmentPayment = createAsyncThunk<
  any,
  ExportGarnishmentPaymentPayload,
  { state: RootState }
>(
  "settings/exportGarnishmentPayment",
  async (payload: ExportGarnishmentPaymentPayload) => {
    const response = await runExportGarnishmentPayment(payload);
    return response.data;
  },
);

export const createTradeUnionPayment = createAsyncThunk<
  any,
  CreateTaxPaymentPayload,
  { state: RootState }
>(
  "settings/createTradeUnionPayment",
  async (payload: CreateTradeUnionPaymentPayload) => {
    const response = await runCreateTradeUnionPayment(payload);
    return response.data;
  },
);

export const exportTradeUnionPayment = createAsyncThunk<
  any,
  ExportTaxPaymentPayload,
  { state: RootState }
>(
  "settings/exportTradeUnionPayment",
  async (payload: ExportTradeUnionPaymentPayload) => {
    const response = await runExportTradeUnionPayment(payload);
    return response.data;
  },
);

export const createPayerSummaryReportDelivery = createAsyncThunk<
  any,
  CreatePayerSummaryReportDeliveryPayload,
  { state: RootState }
>(
  "settings/createPayerSummaryReportDelivery",
  async (payload: CreatePayerSummaryReportDeliveryPayload) => {
    const response = await runCreatePayerSummaryReportDelivery(payload);
    return response.data;
  },
);

export const updatePayerSummaryReportDeliveryStatus = createAsyncThunk<
  any,
  UpdatePayerSummaryReportDeliveryStatusPayload,
  { state: RootState }
>(
  "settings/updatePayerSummaryReportDeliveryStatus",
  async (payload: UpdatePayerSummaryReportDeliveryStatusPayload) => {
    const response = await runUpdatePayerSummaryReportDeliveryStatus(payload);
    return response.data;
  },
);

export const createWageReportDelivery = createAsyncThunk<
  any,
  CreateWageReportDeliveryPayload,
  { state: RootState }
>(
  "settings/createWageReportDelivery",
  async (payload: CreateWageReportDeliveryPayload) => {
    const response = await runCreateWageReportDelivery(payload);
    return response.data;
  },
);

export const updateWageReportDeliveryStatus = createAsyncThunk<
  any,
  UpdateWageReportDeliveryStatusPayload,
  { state: RootState }
>(
  "settings/updateWageReportDeliveryStatus",
  async (payload: UpdateWageReportDeliveryStatusPayload) => {
    const response = await runUpdateWageReportDeliveryStatus(payload);
    return response.data;
  },
);

export const deleteWageReportDelivery = createAsyncThunk<
  any,
  DeleteWageReportDeliveryPayload,
  { state: RootState }
>(
  "settings/deleteWageReportDelivery",
  async (payload: DeleteWageReportDeliveryPayload) => {
    const response = await runDeleteWageReportDelivery(payload);
    return response.data;
  },
);

export const acceptWorkAgreement = createAsyncThunk<
  any,
  AcceptWorkAgreementPayload,
  { state: RootState }
>(
  "settings/acceptWorkAgreement",
  async (payload: AcceptWorkAgreementPayload) => {
    const response = await runAcceptWorkAgreement(payload);
    return response.data;
  },
);

export const fetchEvents = createAsyncThunk<
  InspectEventsResponse[],
  InspectEventsFormValues,
  { state: RootState }
>(
  "settings/fetchEvents",
  async (filters: InspectEventsFormValues, { requestId }) => {
    const { service, ...rest } = filters;
    return await inspectEvents(rest, service, { requestId });
  },
);

export const fetchTesPriceList = createAsyncThunk<
  TesPriceList[],
  { tesId?: string; date: string },
  { state: RootState }
>("settings/fetchTesPriceList", async ({ tesId, date }) => {
  return await getTesPriceList({ tesId, date });
});

export const updateTesPriceList = createAsyncThunk<
  UpdateTesPriceListEvent[],
  UpdateTesPriceList,
  { state: RootState }
>("settings/updateTesPriceList", async (payload: UpdateTesPriceList) => {
  const response = await runUpdateTesPriceList(payload);
  return response.data;
});

export const cancelShift = createAsyncThunk<
  any,
  CancelShift,
  { state: RootState }
>(
  "settings/cancelShift",
  async ({ assignmentId, paySalary, reason }: CancelShift) => {
    return await runCancelShift({ assignmentId, paySalary, reason });
  },
);

export const setTalentTemporaryPassword = createAsyncThunk<
  TempPasswordUpdated,
  SetTalentTemporaryPassword,
  { state: RootState }
>(
  "settings/setTalentTemporaryPassword",
  async ({ talentId }: SetTalentTemporaryPassword) => {
    const response = await runSetTalentTemporaryPassword({ talentId });
    return response.data;
  },
);

export const updateInvoice = createAsyncThunk<
  any,
  UpdateInvoicePayload,
  { state: RootState }
>("settings/updateInvoice", async (payload: UpdateInvoicePayload) => {
  const response = await runUpdateInvoice(payload);
  return response.data;
});

export const updateTalentStatus = createAsyncThunk<
  any,
  UpdateTalentStatusPayload,
  { state: RootState }
>("settings/updateTalentStatus", async (payload: UpdateTalentStatusPayload) => {
  const response = await runUpdateTalentStatus(payload);
  return response.data;
});

export const updateTalentEmail = createAsyncThunk<
  any,
  UpdateTalentEmailAddressPayload,
  { state: RootState }
>(
  "settings/updateTalentEmailAddress",
  async (payload: UpdateTalentEmailAddressPayload) => {
    const response = await runUpdateTalentEmailAddress(payload);
    return response.data;
  },
);

export const acceptWorkOffer = createAsyncThunk<
  any,
  AcceptWorkOffer,
  { state: RootState }
>(
  "settings/acceptWorkOffer",
  async ({ bundleId, talentId, startingShiftAt }) => {
    return await runAcceptWorkOffer({ bundleId, talentId, startingShiftAt });
  },
);

export const processWorkOffers = createAsyncThunk<
  any,
  ProcessWorkOffersPayload,
  { state: RootState }
>("settings/processWorkOffers", async ({ workOffersId }) => {
  return await runProcessWorkOffers({ workOffersId });
});

export const deleteInvoice = createAsyncThunk<
  any,
  UpdateInvoicePayload,
  { state: RootState }
>("settings/deleteInvoice", async (payload: UpdateInvoicePayload) => {
  const response = await runDeleteInvoice(payload);
  return response.data;
});
